<!--
  Capacità driver per provincia.

  I driver sono a chiamata: coprire una provincia non vuol dire essere
  disponibili. Qui Simba indica quanti driver e quanti jolly garantisce
  CONTEMPORANEAMENTE in ogni provincia; il planner non vende orari oltre quel
  numero.

  Come conta il backend (vedi capacityAPI.js):
   - occupano la provincia TUTTE le corse, anche senza driver assegnato: le
     prenotazioni delle sedi della provincia e gli spostamenti interni che
     partono da lì;
   - pickup e delivery impegnano un driver e un jolly, il transfer e l'auto di
     cortesia solo il driver, gli spostamenti a 2 operatori entrambi;
   - ogni corsa occupa fino alla sua fine + 5 minuti.
  Campo vuoto = nessun limite: la provincia funziona come prima.

  Di default si vedono solo le province che interessano (con sedi di partner,
  driver operativi o una capacità già impostata); le altre con «Tutte».
-->
<template>
  <div class="cap">
    <div class="cap__head">
      <div>
        <h1 class="cap__title">
          Capacità driver
        </h1>
        <p class="cap__sub">
          Quanti driver e jolly Simba garantisce contemporaneamente in ogni provincia.
          Vuoto = nessun limite.
        </p>
      </div>
      <div class="cap__headActions">
        <el-input
          v-model="search"
          class="cap__search"
          placeholder="Cerca provincia o sigla…"
          clearable
        />
        <el-checkbox v-model="showAll">
          Tutte le province
        </el-checkbox>
      </div>
    </div>

    <div
      v-if="forbidden"
      class="cap__warn"
    >
      <b>Sezione riservata a Simba.</b>
      La capacità è un dato interno: il backend la mostra e la modifica solo ai
      ruoli globali (superadmin).
    </div>

    <div class="cap__card">
      <el-table
        v-loading="loading"
        :data="visibleRows"
        style="width: 100%"
        :empty-text="loading ? 'Caricamento…' : 'Nessuna provincia'"
      >
        <el-table-column
          label="Provincia"
          min-width="200"
        >
          <template #default="scope">
            <div class="cap__name">
              {{ scope.row.name }} <span class="cap__meta">{{ scope.row.sigla }}</span>
            </div>
            <div class="cap__meta">
              {{ scope.row.partners ? `${scope.row.partners} partner con sede` : 'nessuna sede di partner' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Driver operativi"
          min-width="150"
        >
          <template #default="scope">
            <span class="cap__num">{{ scope.row.drivers }}</span> driver ·
            <span class="cap__num">{{ scope.row.jolly }}</span> jolly
          </template>
        </el-table-column>

        <el-table-column
          label="Driver contemporanei"
          width="180"
        >
          <template #default="scope">
            <el-input
              v-model="scope.row.draftDriver"
              type="number"
              min="0"
              step="1"
              size="small"
              placeholder="Nessun limite"
              :aria-label="`${scope.row.name}: driver contemporanei`"
            />
          </template>
        </el-table-column>

        <el-table-column
          label="Jolly contemporanei"
          width="180"
        >
          <template #default="scope">
            <el-input
              v-model="scope.row.draftJolly"
              type="number"
              min="0"
              step="1"
              size="small"
              placeholder="Nessun limite"
              :aria-label="`${scope.row.name}: jolly contemporanei`"
            />
          </template>
        </el-table-column>

        <el-table-column
          label=""
          width="120"
          align="right"
        >
          <template #default="scope">
            <el-button
              type="primary"
              size="small"
              :disabled="!changed(scope.row)"
              :loading="savingId === scope.row.id"
              @click="save(scope.row)"
            >
              Salva
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { ElMessage } from 'element-plus';
import capacityAPI from '../../services/capacityAPI.js';
import areasAPI from '../../services/areasAPI.js';

const toDraft = (v) => (v === null || v === undefined ? '' : String(v));
const fromDraft = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

export default {
  name: 'DriverCapacityPage',

  setup() {
    const rows = ref([]);
    const loading = ref(false);
    const forbidden = ref(false);
    const search = ref('');
    const showAll = ref(false);
    const savingId = ref('');

    const load = async () => {
      loading.value = true;
      forbidden.value = false;
      try {
        // Sedi e driver servono solo a orientarsi: se non arrivano la pagina
        // resta utilizzabile.
        const [capacities, partnerProvinces, drivers] = await Promise.all([
          capacityAPI.getCapacities(),
          areasAPI.getPartnerProvinces().catch(() => []),
          capacityAPI.getOperativeDriversByProvince().catch(() => new Map()),
        ]);
        const partners = new Map(partnerProvinces.map((p) => [p.id, p.partnerIds.length]));
        rows.value = capacities.map((p) => ({
          ...p,
          partners: partners.get(p.id) || 0,
          drivers: drivers.get(p.id)?.drivers || 0,
          jolly: drivers.get(p.id)?.jolly || 0,
          draftDriver: toDraft(p.driver_capacity),
          draftJolly: toDraft(p.jolly_capacity),
        }));
      } catch (e) {
        if (e?.response?.status === 403) forbidden.value = true;
        else ElMessage.error('Capacità non caricate');
        console.error('Capacità driver:', e);
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);

    const relevant = (r) =>
      r.partners > 0 || r.drivers > 0 || r.jolly > 0 || r.driver_capacity !== null || r.jolly_capacity !== null;

    const visibleRows = computed(() => {
      const q = search.value.trim().toLowerCase();
      return rows.value.filter(
        (r) => (showAll.value || relevant(r)) && (!q || r.name.toLowerCase().includes(q) || r.sigla.toLowerCase() === q),
      );
    });

    const changed = (r) =>
      fromDraft(r.draftDriver) !== r.driver_capacity || fromDraft(r.draftJolly) !== r.jolly_capacity;

    const valid = (v) => v === null || (Number.isInteger(v) && v >= 0);

    const save = async (row) => {
      const driver = fromDraft(row.draftDriver);
      const jolly = fromDraft(row.draftJolly);
      if (!valid(driver) || !valid(jolly)) {
        ElMessage.error('Serve un numero intero da 0 in su, oppure vuoto per nessun limite.');
        return;
      }
      savingId.value = row.id;
      try {
        const saved = await capacityAPI.setCapacity(row.id, { driver_capacity: driver, jolly_capacity: jolly });
        Object.assign(row, {
          driver_capacity: saved.driver_capacity,
          jolly_capacity: saved.jolly_capacity,
          draftDriver: toDraft(saved.driver_capacity),
          draftJolly: toDraft(saved.jolly_capacity),
        });
        ElMessage.success(`Capacità di ${row.name} salvata`);
      } catch (e) {
        const detail = e?.response?.data?.message;
        ElMessage.error(detail ? `Non salvata: ${Array.isArray(detail) ? detail.join(' · ') : detail}` : 'Non salvata');
      } finally {
        savingId.value = '';
      }
    };

    return { rows, loading, forbidden, search, showAll, savingId, visibleRows, changed, save };
  },
};
</script>

<style scoped>
.cap { padding: 4px 0 32px; }

.cap__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.cap__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.cap__sub { margin: 4px 0 0; font-size: 13px; color: var(--text-tertiary); max-width: 70ch; }
.cap__headActions { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.cap__search { width: 240px; }

.cap__warn {
  margin-bottom: 14px;
  padding: 10px 13px;
  border-radius: 9px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--state-attn);
  background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}

.cap__card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.cap__name { font-weight: 600; color: var(--text-primary); }
.cap__meta { font-size: 12px; font-weight: 400; color: var(--text-tertiary); }
.cap__num { font-variant-numeric: tabular-nums; font-weight: 600; }

/* Intestazione grigia come nelle altre liste (element-overrides.css impone il
   gradiente con !important). */
.cap :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 11px 12px;
  border-bottom: 1px solid var(--border-color);
}
</style>
